import React, { useState } from 'react';
import { submitQuestion } from '../services/api';

interface FormData {
  userName: string;
  userEmail: string;
  question: string;
  category: string;
}

interface QuestionSubmissionFormProps {
  onClose: () => void;
}

const QuestionSubmissionForm: React.FC<QuestionSubmissionFormProps> = ({ onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    userName: '',
    userEmail: '',
    question: '',
    category: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.userName.trim()) {
      newErrors.push('Name is required');
    } else if (!/^[a-zA-Z\s'-]{2,50}$/.test(formData.userName.trim())) {
      newErrors.push('Please enter a valid name (letters, spaces, apostrophes, and hyphens only, 2-50 characters)');
    } else if (/('|--|;|\/\*|\*\/|xp_)/i.test(formData.userName)) {
      newErrors.push('Invalid characters detected in name');
    }

    if (!formData.userEmail.trim()) {
      newErrors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
      newErrors.push('Please enter a valid email address');
    }

    if (!formData.question.trim()) {
      newErrors.push('Question is required');
    } else if (formData.question.trim().length < 10) {
      newErrors.push('Question must be at least 10 characters long');
    } else if (/('|--|;|\/\*|\*\/|xp_)/i.test(formData.question)) {
      newErrors.push('Invalid characters detected in question');
    }

    if (!formData.category) {
      newErrors.push('Please select a category');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await submitQuestion(formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting question:', error);
      setErrors(['Failed to submit question. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="question-submission-form">
        <div className="form-container">
          <div className="success-message">
            <h3>Question Submitted Successfully!</h3>
            <p>Thank you for your question. You'll receive a confirmation email shortly, and I'll get back to you as soon as possible.</p>
            <button type="button" className="btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="question-submission-form">
      <div className="form-container">
        <div className="form-header">
          <h3>Ask Clement a Question</h3>
          <button type="button" className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {errors.length > 0 && (
            <div className="error-list">
              {errors.map((error, index) => (
                <div key={index} className="error-message">
                  {error}
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="userName">Your Name *</label>
            <input
              type="text"
              id="userName"
              name="userName"
              value={formData.userName}
              onChange={handleInputChange}
              required
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label htmlFor="userEmail">Your Email *</label>
            <input
              type="email"
              id="userEmail"
              name="userEmail"
              value={formData.userEmail}
              onChange={handleInputChange}
              required
              placeholder="your.email@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="general">General Question</option>
              <option value="AI">AI</option>
              <option value="dotnet">.NET</option>
              <option value="cloud">Azure, AWS, GCP</option>
              <option value="devops">DevOps</option>
              <option value="architecture">Software Architecture</option>
              <option value="career">Career Advice</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="question">Your Question *</label>
            <textarea
              id="question"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              required
              placeholder="Please describe your question in detail..."
              rows={6}
            />
            <div className="character-count">
              {formData.question.length} characters
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionSubmissionForm;